import { useEffect } from "react";

/**
 * Whether the page on screen has edits that are not saved, so Djinn never navigates away from them.
 *
 * Two sources. A page that knows its own dirty state says so with `useHoldsUnsavedEdits(dirty)`.
 * For every page that does not, any typing into a field in the main area since the page was shown
 * counts, until the form is submitted or the page changes. That is conservative on purpose: a filter
 * field someone typed in also holds Djinn back, which costs a page not followed and never a lost
 * edit. Search boxes and Djinn's own message box are not edits.
 */

let held = 0;
/** The path of the page a field was typed in on, or null: typing belongs to the page it happened on. */
let typedOn: string | null = null;
/** How many have asked to watch (the frame, from the first page; the follow hook while the dock is open). */
let watchers = 0;
let unwatch: (() => void) | null = null;

const here = (): string => (typeof window === "undefined" ? "" : window.location.pathname);

/** A page registers while it holds edits. */
export function useHoldsUnsavedEdits(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    held += 1;
    return () => {
      held -= 1;
    };
  }, [dirty]);
}

const inMain = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest("main") !== null;

/** A field that holds an edit: not a search box, and not Djinn's own message box (the home page's). */
const field = (target: EventTarget | null): boolean =>
  (target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement) &&
  !(target instanceof HTMLInputElement && target.type === "search") &&
  target.closest('[data-djinn="composer-box"], [role="search"]') === null;

/**
 * Start watching the main area's fields; returns the stop. The frame starts it on the first page, so an
 * edit typed before the dock was opened still counts; the follow hook asks too. One set of listeners
 * serves every caller.
 */
export function watchTyping(): () => void {
  watchers += 1;
  if (watchers === 1) {
    const onInput = (event: Event) => {
      if (inMain(event.target) && field(event.target)) typedOn = here();
    };
    const onSubmit = (event: Event) => {
      if (inMain(event.target)) typedOn = null;
    };
    document.addEventListener("input", onInput, true);
    document.addEventListener("submit", onSubmit, true);
    unwatch = () => {
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    watchers -= 1;
    if (watchers === 0) {
      unwatch?.();
      unwatch = null;
    }
  };
}

/** The page changed: what was typed belongs to the page that went. */
export function pageChanged(): void {
  typedOn = null;
}

/** A page that says it is dirty, or typing on the page on screen (a page left since is not held). */
export function hasUnsavedEdits(): boolean {
  return held > 0 || (typedOn !== null && typedOn === here());
}

/** Test seam. */
export function resetGuard(): void {
  held = 0;
  typedOn = null;
}
